/**
 * What does this house get, and what colour?
 *
 * Asked before a siding estimate is generated. Jonathon, 2026-09-30:
 * "template should ask rep before generating the estimate if we're going to
 * be replacing the soffit, fascia, wrapping doors and windows", and the vent
 * and block counts "just need to pop up" for the rep to edit. Each answer
 * changes what is ordered and what the crew is paid, not only how much; the
 * colours carry onto the material order. See packages/core/src/siding.ts.
 *
 * The same shape as the flat roof and metal panel forms, for the same reason.
 */

import {
  SIDING_COUNT_LABELS,
  SIDING_COUNT_SOURCES,
  SIDING_LINES,
  sidingMeasurementWarnings,
  type SidingAnswers,
  type WallMeasurements,
} from "@roof-hawk/core";

import { t } from "../../i18n";

/** Parkview's colours, from the chart the customer is shown. */
const PANEL_COLORS: readonly string[] =
  SIDING_LINES.find((line) => line.key === "parkview")?.viewer.slides.map(
    (slide) => slide.name,
  ) ?? [];

export function SidingQuestions({
  answers,
  onChange,
  walls = [],
}: {
  answers: SidingAnswers;
  onChange: (answers: SidingAnswers) => void;
  /** The measured walls, read only to say what is missing. */
  walls?: readonly WallMeasurements[];
}) {
  const set = <K extends keyof SidingAnswers>(
    key: K,
    value: SidingAnswers[K],
  ) => onChange({ ...answers, [key]: value });

  const warnings = sidingMeasurementWarnings(answers, walls);

  const colorField = (
    label: string,
    key: "panelColor" | "trimColor",
    hint: string,
  ) => {
    const value = answers[key];
    const listed = PANEL_COLORS.includes(value);
    return (
      <label className="flatq-row">
        <span>{t(label)}</span>
        <select
          aria-label={t(label)}
          value={listed ? value : value === "" ? "" : "__other"}
          onChange={(e) => {
            const next = e.target.value;
            if (next === "__other") {
              if (listed || value === "") set(key, " ");
              return;
            }
            set(key, next);
          }}
        >
          <option value="">{t("Choose…")}</option>
          {PANEL_COLORS.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
          <option value="__other">{t("Another colour…")}</option>
        </select>
        {!listed && value !== "" && (
          <input
            aria-label={t("{label}, typed", { label: t(label) })}
            placeholder={t("Type the colour")}
            value={value.trim() === "" ? "" : value}
            onChange={(e) => set(key, e.target.value || " ")}
          />
        )}
        <span className="flatq-hint">{t(hint)}</span>
      </label>
    );
  };

  return (
    <div className="flatq">
      <h3>{t("What does this house get?")}</h3>

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.removal}
          onChange={(e) => set("removal", e.target.checked)}
        />
        {t("Tear off the existing siding")}
      </label>
      <p className="flatq-hint">
        {answers.removal
          ? t(
              "The crew's tear-off rate replaces the layover rate, and a dump trailer goes on the work order per 13 squares coming off.",
            )
          : t(
              "A layover: the new siding goes over what is there, and the crew is paid the layover rate per square to hang it.",
            )}
      </p>

      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.wrapOpenings}
          onChange={(e) => set("wrapOpenings", e.target.checked)}
        />
        {t("Wrap the windows and doors")}
      </label>
      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.soffit}
          onChange={(e) => set("soffit", e.target.checked)}
        />
        {t("Replace the soffit with vinyl")}
      </label>
      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.fascia}
          onChange={(e) => set("fascia", e.target.checked)}
        />
        {t("Wrap the fascia")}
      </label>
      <label className="flatq-check">
        <input
          type="checkbox"
          checked={answers.frieze}
          onChange={(e) => set("frieze", e.target.checked)}
        />
        {t("Wrap the frieze board")}
      </label>
      <p className="flatq-hint">
        {t(
          "Each adds its materials and its charge — per opening for the wraps, by the foot for the soffit, fascia and frieze — and is said in the scope of work.",
        )}
      </p>

      {colorField(
        "Siding colour",
        "panelColor",
        "Goes on the panels on the material order.",
      )}
      {colorField(
        "Trim colour",
        "trimColor",
        "Corners, J channel, vents and blocks. Often white.",
      )}

      <h3>{t("How many of each?")}</h3>
      <p className="flatq-hint">
        {t(
          "Counted on the house, not by the report. The vents start at Hover’s count; the rest at none.",
        )}
      </p>
      {SIDING_COUNT_SOURCES.map((source) => (
        <label className="flatq-row" key={source}>
          <span>{t(SIDING_COUNT_LABELS[source])}</span>
          <input
            type="number"
            min={0}
            step={1}
            aria-label={t(SIDING_COUNT_LABELS[source])}
            value={answers.counts[source]}
            onChange={(e) =>
              set("counts", {
                ...answers.counts,
                [source]: Math.max(0, Math.round(Number(e.target.value) || 0)),
              })
            }
          />
        </label>
      ))}

      {warnings.map((warning) => (
        <div
          className="config-warn"
          role="alert"
          key={warning}
          style={{ marginTop: 8 }}
        >
          {t(warning)}
        </div>
      ))}
    </div>
  );
}
